import { useEffect, useState } from "react";
import { TIME_ZONES } from "@krut/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useSettings } from "../hooks.ts";
import { browserZone } from "../lib/cron.ts";
import { Button, Card, ErrorText, Field, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";

type SettingsInput = { timezone?: string | null; toolUpdates?: boolean };

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (json: SettingsInput) => unwrap(api.me.settings.$put({ json })),
    onSuccess: (data) => {
      qc.setQueryData(["settings"], data);
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** "Asia/Kolkata (GMT+5:30)". */
function zoneLabel(zone: string): string {
  try {
    const off = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value;
    return off ? `${zone.replace(/_/g, " ")} (${off})` : zone;
  } catch {
    return zone;
  }
}


export function SettingsPage() {
  const settings = useSettings();
  const save = useSaveSettings();
  const local = browserZone();
  const [zone, setZone] = useState<string>("");
  // Follow the saved zone only when it changes (saving the other settings must not undo a pick).
  const savedZone = settings.data?.timezone;
  useEffect(() => {
    if (savedZone !== undefined) setZone(savedZone ?? local);
  }, [savedZone, local]);
  // Only zones the assistant knows (the server refuses others).
  const zones = TIME_ZONES;

  if (settings.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (settings.error) return <ErrorText error={settings.error} />;
  const saved = settings.data.timezone;
  const now = (z: string) => {
    try {
      return new Date().toLocaleTimeString(undefined, { timeZone: z, hour: "numeric", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <>
      <PageHeader title="Settings" description="Your own preferences for how your assistant works with you." />

      <h2 className="mb-3 text-base font-semibold">Time zone</h2>
      <Card className="mb-8 space-y-4 p-5">
        <Field
          label="Your time zone"
          hint={<>Your assistant uses it for scheduled tasks (a task “every day at 9am” runs at 9am here), for “today” and “tomorrow”, and for the dates it writes. Saving restarts your assistant, which takes about a minute.</>}
        >
          <Select value={zone} onChange={(e) => setZone(e.target.value)} className="max-w-md">
            {zones.map((z) => <option key={z} value={z}>{zoneLabel(z)}</option>)}
          </Select>
        </Field>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Button variant="primary" disabled={save.isPending || !zone || zone === saved} onClick={() => save.mutate({ timezone: zone })}>
            {save.isPending ? "Saving…" : "Save time zone"}
          </Button>
          {zone !== local && (
            <button type="button" className="text-[var(--color-accent)] hover:underline" onClick={() => setZone(local)}>
              Use this browser's time zone ({local.replace(/_/g, " ")})
            </button>
          )}
          <span className="text-[var(--muted)]">
            {saved ? <>Saved: {saved.replace(/_/g, " ")}, now {now(saved)}.</> : <>Not set yet — your assistant is on UTC.</>}
          </span>
        </div>
      </Card>

      <h2 className="mb-3 text-base font-semibold">Chat apps</h2>
      <Card className="mb-8 px-5 py-3">
        <Switch
          label="Show live progress"
          hint="In Slack, Discord and WhatsApp, while your assistant works the chat shows each step it takes (like “Searching the web — …”) instead of just “Working on it”. In a shared Slack channel, others in the thread see the steps too. You can also send “updates on” or “updates off” in the chat."
          checked={settings.data.toolUpdates}
          disabled={save.isPending}
          onChange={(v) => save.mutate({ toolUpdates: v })}
        />
      </Card>
      <ErrorText error={save.error} />
    </>
  );
}
